import { ActionType, FrameStencil, HandoffAction, PlayerAction, PossessionAction } from "../../../types";
import { sliceItemUpdate } from "../../splice-item";
import { ClosestToHandoffType, findClosestToHandoff } from "../find-closest";
import { frameStencilController } from "../frame-stencil-controller";
import {
  getActionsFromMovements,
  getBallMarkers,
  getPlayerActions,
  getPlayerMarkers,
  getPossessionActionsNoNextBall,
  getReceivingHandoffLayerType,
} from "../frame-stencil-filters";
import { getDurationFromAction } from "../get-duration-from-action";
import { refreshMovements } from "../refresh-movements";
import { getHandoffStartTimes } from "./get-handoff-start-times";

export interface GetActionIntersectionTimeProps {
  action: PlayerAction | PossessionAction;
  pathPercentage: number;
}
export const getActionIntersectionTime = ({ action, pathPercentage }: GetActionIntersectionTimeProps): number => {
  const { startTime, endTime } = action;
  const duration = endTime - startTime;
  return startTime + duration * pathPercentage;
};

export interface AddHandoffProps {
  action: HandoffAction;
  stencils: FrameStencil[];
}

export const addHandoff = ({ action, stencils }: AddHandoffProps): FrameStencil[] => {
  /** ================================
   * Get Closest Paths
   ================================ */
  // get all ball markers
  const ballMarkers = getBallMarkers(stencils);
  const playerMarkers = getPlayerMarkers(stencils);

  // get all players with possession that don't have a possession movement as their first movement
  const playersWithPossession = playerMarkers.filter((p) => {
    // get movement actions
    const actions = getActionsFromMovements({ stencils, movements: p.movements });
    // if the first action is a possession action, return false
    if (actions.length > 0 && actions[0]?.actionType === ActionType.POSSESSION) return false;

    // get the ball marker associated with this player
    const ballOnPlayer = ballMarkers.find((b) => b.associatedPlayer === p.id);

    // if no ball on player, then player doesn't have possession
    if (!ballOnPlayer) return false;

    // if the ball marker's first movement is a pass, return false
    const ballActions = getActionsFromMovements({ stencils, movements: ballOnPlayer.movements });
    if (ballActions.length > 0 && ballActions[0]?.actionType === ActionType.BALL) return false;

    return true;
  });

  /** find the closest prev possession to the handoff, either a player with possession or a possession action */
  const closestPossessionToHandoff = findClosestToHandoff({
    target: action.pos,
    pathStencils: getPossessionActionsNoNextBall(stencils),
    playerStencils: playersWithPossession,
  });

  // closest player path
  const playersWithoutPossession = playerMarkers.filter((p) => {
    const ballOnPlayer = ballMarkers.find((b) => b.associatedPlayer === p.id);
    return !ballOnPlayer;
  });

  // get all players that aren't associated with the found possession closest to handoff
  const playersNotAssociatedWithPossessionHandoff = getPlayerActions(stencils).filter((s) => {
    // if no possession found, return true
    if (!closestPossessionToHandoff) return true;
    switch (closestPossessionToHandoff.type) {
      // path possession..
      case ClosestToHandoffType.PATH: {
        // if possession action's nextAction is this playerAction, return false
        if (closestPossessionToHandoff.actionWithIntersection.action.nextPlayerAction === s.id) return false;
        // if this playerAction's nextAction is the possession action, return false
        else if (s.nextPlayerAction === closestPossessionToHandoff.actionWithIntersection.action.id) return false;
        // else return true
        else return true;
      }
      // player with possession..
      case ClosestToHandoffType.PLAYER: {
        // if player with possession is on this playerAction, return false
        if (closestPossessionToHandoff.player.movements.some((m) => m.id === s.id)) return false;
        // else return true
        else return true;
      }
    }
  });

  /** find the closest next player to the handoff, either a player without possession, or a player action */
  const closestPlayerToHandoff = findClosestToHandoff({
    target: action.pos,
    pathStencils: playersNotAssociatedWithPossessionHandoff,
    playerStencils: playersWithoutPossession,
  });

  const duration = getDurationFromAction(action);

  /** ======== Path Start Times ========
   * possessionIntersection is closest intersection on possession path
   */
  const { possessionStartTime, playerStartTime, handoffStartTime } = getHandoffStartTimes({
    possessionIntersection:
      closestPossessionToHandoff?.type === ClosestToHandoffType.PATH
        ? closestPossessionToHandoff.actionWithIntersection
        : null,
    playerIntersection:
      closestPlayerToHandoff?.type === ClosestToHandoffType.PATH ? closestPlayerToHandoff.actionWithIntersection : null,
    handoff: action,
  });

  // if receiving end of handoff is a player marker, shift the player's first movement to the handoff start time
  const stencilsWithUpdatedPlayerMovementStartTimes = (() => {
    if (!closestPlayerToHandoff || closestPlayerToHandoff.type !== ClosestToHandoffType.PLAYER) return stencils;
    const playerActions = getActionsFromMovements({ stencils, movements: closestPlayerToHandoff.player.movements });

    if (playerActions[0]) {
      const playerActionDuration = playerActions[0].endTime - playerActions[0].startTime;
      return sliceItemUpdate({
        item: {
          ...playerActions[0],
          startTime: handoffStartTime + duration,
          endTime: playerActionDuration + handoffStartTime + duration,
        },
        arr: stencils,
      });
    } else return stencils;
  })();

  /** ================================
   * Handoff Possession Start
   ================================ */
  /** ======== Update Possession ======== */
  const stencilsWithUpdatedPrevStencil = (() => {
    // if no closest possession, return stencils
    if (!closestPossessionToHandoff) return stencilsWithUpdatedPlayerMovementStartTimes;

    switch (closestPossessionToHandoff.type) {
      // if path..
      case ClosestToHandoffType.PATH: {
        const { action: possessionAction } = closestPossessionToHandoff.actionWithIntersection;
        const { startTime, endTime } = possessionAction;
        const possessionDuration = endTime - startTime;

        // update possession action with start and end times, and handoff as next action
        return sliceItemUpdate({
          item: {
            ...possessionAction,
            nextBallAction: action.id,
            startTime: possessionStartTime,
            endTime: possessionStartTime + possessionDuration,
          },
          arr: stencilsWithUpdatedPlayerMovementStartTimes,
        });
      }
      // if player..
      case ClosestToHandoffType.PLAYER:
        return stencilsWithUpdatedPlayerMovementStartTimes;
    }
  })();

  /** ======== Update Handoff ======== */
  const handoffWithPrevPlayer = ((): HandoffAction => {
    if (!closestPossessionToHandoff) return action;

    switch (closestPossessionToHandoff.type) {
      // possession path..
      case ClosestToHandoffType.PATH: {
        const { action: possessionAction, intersection } = closestPossessionToHandoff.actionWithIntersection;
        // update handoff with start and end times, possession as prev action, and possession intersection as start pos
        const handoffWithStartPos = {
          ...action,
          prevAction: possessionAction.id,
          startPos: intersection,
          startTime: handoffStartTime,
          endTime: handoffStartTime + duration,
        };

        // find player on possession action
        const playerOnPossessionAction = getPlayerMarkers(stencilsWithUpdatedPrevStencil).find((p) =>
          p.movements.some((m) => m.id === possessionAction.id)
        );

        // if player on possession action, add it to handoff's prevPlayer
        if (!playerOnPossessionAction) return handoffWithStartPos;
        else return { ...handoffWithStartPos, prevPlayer: playerOnPossessionAction.id };
      }
      // player with possession..
      case ClosestToHandoffType.PLAYER: {
        // update handoff with start and end times, player as prev player, and player pos as start pos
        return {
          ...action,
          prevPlayer: closestPossessionToHandoff.player.id,
          startPos: closestPossessionToHandoff.player.pos,
          startTime: handoffStartTime,
          endTime: handoffStartTime + duration,
        };
      }
    }
  })();

  /** ================================
   * Handoff Player End
   ================================ */
  /** ======== Update Player ======== */
  const stencilsWithUpdatedNextStencil = (() => {
    // if no closest player, return stencils
    if (!closestPlayerToHandoff) return stencilsWithUpdatedPrevStencil;

    switch (closestPlayerToHandoff.type) {
      // if path..
      case ClosestToHandoffType.PATH: {
        const { action: playerAction } = closestPlayerToHandoff.actionWithIntersection;
        const playerActionDuration = playerAction.endTime - playerAction.startTime;

        // update player action with start and end times
        return frameStencilController({
          stencils: stencilsWithUpdatedPrevStencil,
          operation: "UPDATE",
          stencil: {
            ...playerAction,
            actionType: ActionType.POSSESSION,
            layerType: getReceivingHandoffLayerType(playerAction.layerType),
            nextBallAction: null,
            startTime: playerStartTime,
            endTime: playerStartTime + playerActionDuration,
          },
        });
      }
      // if player..
      case ClosestToHandoffType.PLAYER:
        return stencilsWithUpdatedPrevStencil;
    }
  })();

  /** ======== Update Handoff ======== */
  const handoffWithNextPlayer = (() => {
    // if no closest player, return handoff with prev player
    if (!closestPlayerToHandoff) return handoffWithPrevPlayer;

    switch (closestPlayerToHandoff.type) {
      // if path..
      case ClosestToHandoffType.PATH: {
        const { action: playerAction, intersection } = closestPlayerToHandoff.actionWithIntersection;
        // update handoff with start and end times, action as nextAction, and intersection as endPos
        const handoffWithEndPos = {
          ...handoffWithPrevPlayer,
          nextAction: playerAction.id,
          endPos: intersection,
          startTime: handoffStartTime,
          endTime: handoffStartTime + duration,
        };

        // find player on player action
        const playerOnPlayerAction = getPlayerMarkers(stencilsWithUpdatedNextStencil).find((p) =>
          p.movements.some((m) => m.id === playerAction.id)
        );

        // if found, add it to handoff's nextPlayer
        if (!playerOnPlayerAction) return handoffWithEndPos;
        else return { ...handoffWithEndPos, nextPlayer: playerOnPlayerAction.id };
      }

      // if player..
      case ClosestToHandoffType.PLAYER: {
        // update handoff with start and end times, player as nextPlayer, and player pos as endPos
        return {
          ...handoffWithPrevPlayer,
          nextPlayer: closestPlayerToHandoff.player.id,
          endPos: closestPlayerToHandoff.player.pos,
          startTime: handoffStartTime,
          endTime: handoffStartTime + duration,
        };
      }
    }
  })();

  // add handoff to stencils
  const stencilsWithUpdatedHandoff = [...stencilsWithUpdatedNextStencil, handoffWithNextPlayer];

  return refreshMovements(stencilsWithUpdatedHandoff);
};
